import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner, Row, Col } from "react-bootstrap";
import Select from "react-select";
import { getAllTasks, getAllRecommendations, createRecommendation } from "../services/APIClient";

function CreateRecommendationModal({ show, onHide, onSuccess, userName, prefillTaskId = null }) {
  const [formData, setFormData] = useState({
    taskId: "",
    name: "",
    url: "",
    description: "",
    countryid: "",
  });

  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [allRecommendations, setAllRecommendations] = useState([]);
  const [selectedGradeLevel, setSelectedGradeLevel] = useState("all");
  const [creationScope, setCreationScope] = useState("task"); // "task" or "group"
  const [selectedTask, setSelectedTask] = useState(null);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (show) {
      fetchTasks();
      fetchAllRecommendations();
      resetForm();
    }
  }, [show]);

  useEffect(() => {
    filterTasks();
  }, [tasks, selectedGradeLevel]);

  useEffect(() => {
    if (prefillTaskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === prefillTaskId);
      if (task) {
        setFormData(prev => ({ ...prev, taskId: task.id }));
        setSelectedTask(task);
      }
    }
  }, [prefillTaskId, tasks]);

  const fetchAllRecommendations = async () => {
    try {
      const data = await getAllRecommendations();
      setAllRecommendations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    }
  };

  const fetchTasks = async () => {
    setIsLoadingTasks(true);
    try {
      const response = await getAllTasks();
      const tasksData = Array.isArray(response) ? response : response.data || [];
      const sortedTasks = tasksData.sort((a, b) => (a.order || 0) - (b.order || 0));
      setTasks(sortedTasks);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setError("Failed to load tasks");
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const filterTasks = () => {
    if (selectedGradeLevel === "all") {
      setFilteredTasks(tasks);
    } else {
      const gradeField = `grade_level${selectedGradeLevel}`;
      const filtered = tasks.filter(task => task[gradeField] === true);
      setFilteredTasks(filtered);
    }
  };

  const resetForm = () => {
    setFormData({
      taskId: "",
      name: "",
      url: "",
      description: "",
      countryid: "",
    });
    setSelectedGradeLevel("all");
    setCreationScope("task");
    setSelectedTask(null);
    setValidationErrors({});
    setError(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: null });
    }
  };

  const handleTaskSelect = (e) => {
    const taskId = e.target.value;
    setFormData({ ...formData, taskId });
    
    const task = tasks.find(t => t.id === taskId);
    setSelectedTask(task);
    
    // Reset to task scope when changing tasks
    setCreationScope("task");
    
    if (validationErrors.taskId) {
      setValidationErrors({ ...validationErrors, taskId: null });
    }
  };

  const validate = () => {
    const errors = {};

    if (!formData.taskId) {
      errors.taskId = "Task is required";
    }

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.url.trim()) {
      errors.url = "URL is required";
    } else if (!formData.url.startsWith("./docs/") && !formData.url.startsWith("http")) {
      errors.url = "URL must start with './docs/' or 'http'";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        url: formData.url,
        description: formData.description,
        countryid: formData.countryid,
        updated_by: userName,
        is_active: true,
      };

      if (creationScope === "group" && selectedTask?.parentTaskid) {
        // Create grouped recommendation
        payload.parentTaskId = selectedTask.parentTaskid;
        payload.name = selectedTask.parentTaskName; // Use parent task name
      } else {
        // Create individual recommendation
        payload.taskId = formData.taskId;
        payload.name = formData.name;
      }

      await createRecommendation(payload);
      onSuccess();
    } catch (error) {
      console.error("Error creating recommendation:", error);
      setError(error.response?.data?.error?.message || "Failed to create recommendation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canApplyToGroup = selectedTask?.parentTaskid;

  // Count sibling tasks (tasks with same parent)
  const getSiblingCount = () => {
    if (!selectedTask?.parentTaskid) return 0;
    return tasks.filter(t => t.parentTaskid === selectedTask.parentTaskid).length;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New Recommendation</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Task <span className="text-danger">*</span>
                </Form.Label>
                {isLoadingTasks ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Loading tasks...</span>
                  </div>
                ) : (
                  <Select
                    options={filteredTasks.map(task => ({ value: task.id, label: task.name, description: task.description, task }))}
                    value={formData.taskId ? { value: formData.taskId, label: selectedTask?.name } : null}
                    onChange={(option) => handleTaskSelect({ target: { value: option ? option.value : "" } })}
                    placeholder="Select a task..."
                    isClearable
                    formatOptionLabel={(option) => (
                      <div>
                        <div>{option.label}</div>
                        {option.description && (
                          <div style={{ fontSize: "0.75rem", fontStyle: "italic", color: "black" }}>
                            ({option.description})
                          </div>
                        )}
                      </div>
                    )}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: validationErrors.taskId ? "#dc3545" : base.borderColor,
                      }),
                    }}
                  />
                )}
                {validationErrors.taskId && (
                  <div className="text-danger" style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
                    {validationErrors.taskId}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Filter by Grade</Form.Label>
                <Form.Select
                  value={selectedGradeLevel}
                  onChange={(e) => setSelectedGradeLevel(e.target.value)}
                >
                  <option value="all">All Grades</option>
                  <option value="1">Grade Level 1</option>
                  <option value="2">Grade Level 2</option>
                  <option value="3">Grade Level 3</option>
                  <option value="4">Grade Level 4</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Show scope selection if task has parent */}
          {canApplyToGroup && (
            <Alert variant="info" className="mb-3">
              <div className="mb-2">
                <strong>This task is part of "{selectedTask.parentTaskName}" group</strong>
                <div className="small text-muted">
                  This group contains {getSiblingCount()} tasks
                </div>
              </div>
              
              <Form.Check
                type="radio"
                id="scope-task"
                name="creationScope"
                value="task"
                checked={creationScope === "task"}
                onChange={(e) => setCreationScope(e.target.value)}
                label={
                  <span>
                    <strong>Create for this task only</strong>
                    <div className="small text-muted">Standalone recommendation for "{selectedTask.name}"</div>
                  </span>
                }
                className="mb-2"
              />
              
              <Form.Check
                type="radio"
                id="scope-group"
                name="creationScope"
                value="group"
                checked={creationScope === "group"}
                onChange={(e) => setCreationScope(e.target.value)}
                label={
                  <span>
                    <strong>Create for all tasks in "{selectedTask.parentTaskName}"</strong>
                    <div className="small text-muted">
                      Group recommendation will be named "{selectedTask.parentTaskName}"
                    </div>
                  </span>
                }
              />
            </Alert>
          )}

          {/* Only show name field for individual recommendations */}
          {creationScope === "task" && (
            <Form.Group className="mb-3">
              <Form.Label>
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Rhyming, Letter Sound Knowledge"
                isInvalid={!!validationErrors.name}
                required
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.name}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                The display name for this recommendation
              </Form.Text>
            </Form.Group>
          )}

          {/* Show parent name info for group recommendations */}
          {creationScope === "group" && (
            <Alert variant="secondary" className="mb-3">
              <strong>Recommendation Name:</strong> {selectedTask.parentTaskName}
              <div className="small text-muted mt-1">
                Grouped recommendations use the parent task name
              </div>
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              PDF URL <span className="text-danger">*</span>
            </Form.Label>
            {allRecommendations.length > 0 && (
              <Select
                className="mb-2"
                options={[...new Map(allRecommendations.map(r => [r.url, r])).values()].map(r => ({
                  value: r.url,
                  label: r.name,
                  url: r.url,
                }))}
                onChange={(option) => option && setFormData(prev => ({ ...prev, url: option.value }))}
                placeholder="Reuse an existing URL..."
                isClearable
                formatOptionLabel={(option) => (
                  <div>
                    <div>{option.label}</div>
                    <div style={{ fontSize: "0.75rem", fontStyle: "italic", color: "black" }}>
                      ({option.url})
                    </div>
                  </div>
                )}
              />
            )}
            <Form.Control
              type="text"
              name="url"
              value={formData.url}
              onChange={handleChange}
              placeholder="./docs/Alpaca_Rhyming_Recommendations.pdf"
              isInvalid={!!validationErrors.url}
              required
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.url}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Path to the PDF file (e.g., ./docs/filename.pdf)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Additional information about this recommendation..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Country ID (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="countryid"
              value={formData.countryid}
              onChange={handleChange}
              placeholder="Leave blank for all countries"
            />
            <Form.Text className="text-muted">
              Only fill this if the recommendation is country-specific
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                {creationScope === "group" 
                  ? `Create Group Recommendation` 
                  : "Create Recommendation"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default CreateRecommendationModal;